import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Star, Pencil, Check, X, GripVertical } from 'lucide-react';
import { useFavorites, FAV_ROLES } from '../../lib/favorites';
import { useAuthStore } from '../../lib/auth';
import { FavIcon, favColor } from './favUi';

function Tile({ fav, catalog, editing, onRemove, idx }) {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: fav.path, disabled: !editing });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`relative ${isDragging ? 'z-10 opacity-80' : ''}`} data-testid={`fav-tile-${idx}`}>
      <button type="button" onClick={() => !editing && navigate(fav.path)} {...(editing ? { ...attributes, ...listeners } : {})}
        className={`group w-full h-full text-left rounded-2xl border border-slate-200/70 dark:border-slate-700/60 bg-white dark:bg-slate-900 p-3 flex items-center gap-3 transition-[box-shadow,transform] ${editing ? 'cursor-grab active:cursor-grabbing touch-none ring-2 ring-dashed ring-amber-300/70' : 'hover:shadow-lg hover:-translate-y-0.5'}`}>
        <span className={`h-10 w-10 shrink-0 rounded-xl bg-gradient-to-br ${favColor(fav.path)} text-white flex items-center justify-center shadow-sm`}>
          <FavIcon fav={fav} catalog={catalog} />
        </span>
        <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 leading-tight line-clamp-2 flex-1">{fav.label}</span>
        {editing && <GripVertical className="h-4 w-4 text-slate-400 shrink-0" />}
      </button>
      {editing && (
        <button type="button" onClick={onRemove} className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-rose-500 text-white shadow flex items-center justify-center hover:bg-rose-600" data-testid={`fav-tile-remove-${idx}`}>
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export function FavoritesBoard() {
  const role = useAuthStore((s) => s.user?.role);
  const { items, catalog, save } = useFavorites();
  const [editing, setEditing] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  if (!FAV_ROLES.includes(role)) return null;
  const visible = items.filter((f) => catalog[f.path]);
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = items.findIndex((i) => i.path === active.id);
    const to = items.findIndex((i) => i.path === over.id);
    save(arrayMove(items, from, to));
  };
  return (
    <section className="space-y-3" data-testid="fav-board">
      <div className="flex items-center gap-2">
        <Star className="h-4 w-4 fill-amber-400 text-amber-500" />
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500">Favorilerim</h2>
        {visible.length > 0 && (
          <button type="button" onClick={() => setEditing((e) => !e)}
            className={`ml-auto h-8 px-3 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${editing ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200'}`}
            data-testid="fav-edit-toggle">
            {editing ? <><Check className="h-3.5 w-3.5" />Bitti</> : <><Pencil className="h-3.5 w-3.5" />Düzenle</>}
          </button>
        )}
      </div>
      {visible.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-700 px-4 py-5 text-sm text-slate-500 flex items-center gap-3" data-testid="fav-empty">
          <Star className="h-5 w-5 text-amber-400 shrink-0" />
          Sık kullandığınız sayfaları açın ve üst çubuktaki yıldıza tıklayın; burada ve sağ alttaki çarkta görünsünler.
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={visible.map((f) => f.path)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
              {visible.map((f, i) => (
                <Tile key={f.path} fav={f} idx={i} catalog={catalog} editing={editing} onRemove={() => save(items.filter((x) => x.path !== f.path))} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      {editing && <p className="text-xs text-slate-500">Kartları tutup sürükleyerek sıralayın; telefonda basılı tutup sürükleyin.</p>}
    </section>
  );
}
